import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import Logo from './Logo';
import { useLanguage, useLanguageNav } from '../contexts/LanguageContext';
import { langToSlug } from '../hooks/useLocalePath';
import type { Language } from '../i18n';

/** Flag SVG for language toggle */
const FlagEN = () => (
    <svg className="lang-flag" viewBox="0 0 18 12" xmlns="http://www.w3.org/2000/svg">
        <rect width="18" height="12" fill="#fff" />
        <rect y="0" width="18" height="1.85" fill="#B22234" />
        <rect y="3.7" width="18" height="1.85" fill="#B22234" />
        <rect y="7.4" width="18" height="1.85" fill="#B22234" />
        <rect y="10.15" width="18" height="1.85" fill="#B22234" />
        <rect width="7.5" height="6.5" fill="#3C3B6E" />
        <circle cx="1.8" cy="1.6" r="0.55" fill="#fff" /><circle cx="3.75" cy="1.6" r="0.55" fill="#fff" />
        <circle cx="5.7" cy="1.6" r="0.55" fill="#fff" /><circle cx="1.8" cy="3.3" r="0.55" fill="#fff" />
        <circle cx="3.75" cy="3.3" r="0.55" fill="#fff" /><circle cx="5.7" cy="3.3" r="0.55" fill="#fff" />
        <circle cx="1.8" cy="5.0" r="0.55" fill="#fff" /><circle cx="3.75" cy="5.0" r="0.55" fill="#fff" />
        <circle cx="5.7" cy="5.0" r="0.55" fill="#fff" />
    </svg>
);

const FlagPT = () => (
    <svg className="lang-flag" viewBox="0 0 18 12" xmlns="http://www.w3.org/2000/svg">
        <rect width="18" height="12" fill="#009C3B" />
        <polygon points="9,1.2 16.8,6 9,10.8 1.2,6" fill="#FFDF00" />
        <circle cx="9" cy="6" r="2.8" fill="#002776" />
        <path d="M6.5 5.8 Q9 4.6 11.5 5.8" stroke="#fff" strokeWidth="0.5" fill="none" strokeLinecap="round" />
    </svg>
);

const FlagES = () => (
    <svg className="lang-flag" viewBox="0 0 18 12" xmlns="http://www.w3.org/2000/svg">
        <rect width="18" height="12" fill="#c60b1e" />
        <rect y="3" width="18" height="6" fill="#ffc400" />
    </svg>
);

const LangToggle: React.FC<{ active: Language; onChange: (l: Language) => void }> = ({ active, onChange }) => (
    <div className="lang-toggle">
        <span className={active === 'EN' ? 'active' : ''} onClick={() => onChange('EN')}>
            <FlagEN /> EN
        </span>
        <div className="lang-sep" />
        <span className={active === 'PT' ? 'active' : ''} onClick={() => onChange('PT')}>
            <FlagPT /> PT
        </span>
        <div className="lang-sep" />
        <span className={active === 'ES' ? 'active' : ''} onClick={() => onChange('ES')}>
            <FlagES /> ES
        </span>
    </div>
);

const Header: React.FC = () => {
    const [menuOpen, setMenuOpen] = React.useState(false);
    const { language, t } = useLanguage();
    const switchLang = useLanguageNav();
    const lp = (path: string) => `/${langToSlug(language)}${path}`;

    const navLinks = [
        { to: lp('/manifesto'), label: t('nav.manifesto') },
        { to: lp('/services'), label: t('nav.services') },
        { to: lp('/industries'), label: t('nav.industries') },
        { to: lp('/contact'), label: t('nav.contact') },
    ];

    const homePath = lp('/');
    const contactPath = lp('/contact#contact-form');

    const closeMenu = () => setMenuOpen(false);

    const scrollToTop = () => {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    };

    // Scroll to contact form even when already on the same page
    const scrollToForm = () => {
        setTimeout(() => {
            document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    return (
        <>
            <header>
                <div className="header-inner">
                    {/* Left: logo + desktop nav */}
                    <div className="header-left">
                        <Link to={homePath} className="nav-logo" onClick={() => { closeMenu(); scrollToTop(); }}>
                            <Logo size={32} />
                            <span className="nav-logo-text">PERCEPT<em>Y</em>A</span>
                        </Link>

                        <nav className="desktop-nav">
                            {navLinks.map(({ to, label }) => (
                                <NavLink
                                    key={to}
                                    to={to}
                                    className={({ isActive }) => (isActive ? 'active' : '')}
                                    onClick={() => scrollToTop()}
                                >
                                    {label}
                                </NavLink>
                            ))}
                        </nav>
                    </div>

                    {/* Desktop right: lang toggle + CTA */}
                    <div className="desktop-right">
                        <LangToggle active={language} onChange={switchLang} />
                        <Link to={contactPath} className="btn-nav" onClick={scrollToForm}>
                            {t('nav.cta')}
                        </Link>
                    </div>

                    {/* Mobile hamburger */}
                    <button
                        className={`hamburger${menuOpen ? ' open' : ''}`}
                        onClick={() => setMenuOpen(v => !v)}
                        aria-label="Menu"
                    >
                        <span /><span /><span />
                    </button>
                </div>
            </header>

            {/* Mobile drawer */}
            <nav className={`mobile-nav${menuOpen ? ' open' : ''}`}>
                {navLinks.map(({ to, label }) => (
                    <NavLink
                        key={to}
                        to={to}
                        className={({ isActive }) => (isActive ? 'active' : '')}
                        onClick={() => { closeMenu(); scrollToTop(); }}
                    >
                        {label}
                    </NavLink>
                ))}
                <div className="mobile-nav-footer">
                    <LangToggle active={language} onChange={switchLang} />
                    <Link to={contactPath} className="btn-nav" onClick={() => { closeMenu(); scrollToForm(); }}>
                        {t('nav.cta')}
                    </Link>
                </div>
            </nav>
        </>
    );
};

export default Header;
